import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAdd, MdClose, MdSave } from 'react-icons/md'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    getVideoSettings,
    updateVideoSettings,
    VIDEO_FEATURE_LABELS,
    type VideoFeature,
    type VideoSettings,
} from '@/lib/api/video/video-api'
import VideoCustomDomainSection from './video-custom-domain-section'
import VideoWebhookSection from './video-webhook-section'

// Reações e sala de espera ainda não existem na sala pronta (apps/meet): ficam fora
// da tela até serem implementadas, para não oferecer um botão que não faz nada.
const FEATURE_ORDER: VideoFeature[] = ['prejoin', 'screenShare', 'chat', 'raiseHand']

function isValidOrigin(value: string): boolean {
    try {
        const url = new URL(value)
        return (url.protocol === 'https:' || url.protocol === 'http:') && url.origin === value.replace(/\/$/, '')
    } catch {
        return false
    }
}

export default function VideoSettingsPage() {
    const { canUpdate } = usePermission()
    const editable = canUpdate('video', 'settings')
    const [settings, setSettings] = useState<VideoSettings | null>(null)
    const [newOrigin, setNewOrigin] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        void getVideoSettings().then(response => {
            if (response.success && response.result) setSettings(response.result)
            else toast.error(response.error || 'Não foi possível carregar as configurações')
        })
    }, [])

    if (!settings) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        )
    }

    const addOrigin = () => {
        const origin = newOrigin.trim().replace(/\/$/, '')
        if (!isValidOrigin(origin)) {
            toast.error('Informe só o endereço do site, ex.: https://app.suaempresa.com.br')
            return
        }
        if (settings.allowedOrigins.includes(origin)) {
            setNewOrigin('')
            return
        }
        setSettings({ ...settings, allowedOrigins: [...settings.allowedOrigins, origin] })
        setNewOrigin('')
    }

    const handleSave = async () => {
        setSaving(true)
        const response = await updateVideoSettings({
            allowedOrigins: settings.allowedOrigins,
            defaultFeatures: settings.defaultFeatures,
            branding: {
                displayName: settings.branding.displayName?.trim() || undefined,
                logoUrl: settings.branding.logoUrl?.trim() || undefined,
                primaryColor: settings.branding.primaryColor || undefined,
            },
        })
        setSaving(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível salvar')
            return
        }
        setSettings(response.result)
        toast.success('Configurações salvas')
    }

    return (
        <div className="p-4 sm:p-6 space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-semibold">Configurações de vídeo</h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Onde a sala pode ser embutida, domínio próprio, recursos ligados por padrão e marca exibida
                </p>
            </div>

            <section className="card bg-base-100 border border-base-200">
                <div className="card-body p-5 space-y-3">
                    <div>
                        <h2 className="font-semibold">Sites autorizados a embutir a sala</h2>
                        <p className="text-sm text-base-content/60">
                            Só estes endereços podem exibir a sala dentro de um iframe. Vazio, a sala só abre
                            pelo link direto.
                        </p>
                    </div>

                    {settings.allowedOrigins.length > 0 && (
                        <ul className="space-y-2">
                            {settings.allowedOrigins.map(origin => (
                                <li
                                    key={origin}
                                    className="flex items-center justify-between gap-2 rounded-lg bg-base-200/60 px-3 py-2"
                                >
                                    <span className="font-mono text-sm break-all">{origin}</span>
                                    {editable && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-ghost text-error"
                                            title="Remover"
                                            onClick={() =>
                                                setSettings({
                                                    ...settings,
                                                    allowedOrigins: settings.allowedOrigins.filter(item => item !== origin),
                                                })
                                            }
                                        >
                                            <MdClose />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    {editable && (
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-origin">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Adicionar site</span>
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="video-origin"
                                    type="url"
                                    className="input input-bordered join-item w-full"
                                    value={newOrigin}
                                    placeholder="https://app.suaempresa.com.br"
                                    onChange={event => setNewOrigin(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault()
                                            addOrigin()
                                        }
                                    }}
                                />
                                <button type="button" className="btn join-item" onClick={addOrigin}>
                                    <MdAdd />
                                    Adicionar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <VideoCustomDomainSection editable={editable} />

            <VideoWebhookSection editable={editable} />

            <section className="card bg-base-100 border border-base-200">
                <div className="card-body p-5 space-y-3">
                    <div>
                        <h2 className="font-semibold">Recursos da sala</h2>
                        <p className="text-sm text-base-content/60">
                            Padrão para todas as salas. Cada sala e cada link gerado pela API podem mudar.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                        {FEATURE_ORDER.map(feature => (
                            <label key={feature} className="label cursor-pointer justify-start items-start gap-3 py-2 whitespace-normal">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-primary toggle-sm"
                                    checked={settings.defaultFeatures[feature]}
                                    disabled={!editable}
                                    onChange={event =>
                                        setSettings({
                                            ...settings,
                                            defaultFeatures: {
                                                ...settings.defaultFeatures,
                                                [feature]: event.target.checked,
                                            },
                                        })
                                    }
                                />
                                <span className="label-text whitespace-normal leading-snug">{VIDEO_FEATURE_LABELS[feature]}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </section>

            <section className="card bg-base-100 border border-base-200">
                <div className="card-body p-5 space-y-4">
                    <div>
                        <h2 className="font-semibold">Marca na sala</h2>
                        <p className="text-sm text-base-content/60">
                            O participante vê o nome e o logo da sua empresa.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-brand-name">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Nome exibido</span>
                                </span>
                            </label>
                            <input
                                id="video-brand-name"
                                type="text"
                                className="input input-bordered w-full"
                                value={settings.branding.displayName ?? ''}
                                maxLength={80}
                                disabled={!editable}
                                onChange={event =>
                                    setSettings({
                                        ...settings,
                                        branding: { ...settings.branding, displayName: event.target.value },
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-brand-color">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Cor principal</span>
                                </span>
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    id="video-brand-color"
                                    type="color"
                                    className="h-12 w-16 cursor-pointer rounded-lg border border-base-300 bg-base-100 p-1"
                                    value={settings.branding.primaryColor || '#4B2E85'}
                                    disabled={!editable}
                                    onChange={event =>
                                        setSettings({
                                            ...settings,
                                            branding: { ...settings.branding, primaryColor: event.target.value },
                                        })
                                    }
                                />
                                <span className="font-mono text-sm text-base-content/60">
                                    {settings.branding.primaryColor || 'Padrão'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="form-control w-full">
                        <label className="label pb-1.5" htmlFor="video-brand-logo">
                            <span className="flex w-full items-baseline justify-between gap-3">
                                <span className="label-text font-medium">Endereço do logo</span>
                                <span className="label-text-alt text-base-content/50">https, opcional</span>
                            </span>
                        </label>
                        <input
                            id="video-brand-logo"
                            type="url"
                            className="input input-bordered w-full"
                            value={settings.branding.logoUrl ?? ''}
                            placeholder="https://suaempresa.com.br/logo.png"
                            disabled={!editable}
                            onChange={event =>
                                setSettings({
                                    ...settings,
                                    branding: { ...settings.branding, logoUrl: event.target.value },
                                })
                            }
                        />
                    </div>
                </div>
            </section>

            {editable && (
                <div className="flex justify-end">
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <span className="loading loading-spinner loading-sm" /> : <MdSave />}
                        Salvar
                    </button>
                </div>
            )}
        </div>
    )
}
